import { CatalogApi } from '@backstage/catalog-client';
import { ConfigApi, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  GithubIntegration,
  ScmIntegrationRegistry,
} from '@backstage/integration';
import { ScmAuthApi } from '@backstage/integration-react';
import { AnalyzeResult, CatalogImportApi } from './CatalogImportApi';
import YAML from 'yaml';
import {
  GitHubOptions,
  submitGitHubPrToRepo,
  addTopicToRepo,
  getRepoTopics,
  createIssueWithCopilotAgent,
  checkCatalogFileExists,
  getRegistrationStatus,
  getRepoOwnerTeams,
  REGISTRATION_ISSUE_LABELS,
  REGISTRATION_ISSUE_TITLE,
} from './GitHub';
import { getCatalogFilename, getBranchName } from '../components/helpers';
import { AnalyzeLocationResponse } from '@backstage/plugin-catalog-common';
import { CompoundEntityRef } from '@backstage/catalog-model';
import parseGitUrl from 'git-url-parse';
import { submitAzurePrToRepo } from './AzureDevops';
import { findBeatIdInTopics } from './beatIdParser';
import { prepareIssueBody, BeatApplication, PlatformResult } from './issueTemplate';

/**
 * The default implementation of the {@link CatalogImportApi}.
 *
 * @public
 */
export class CatalogImportClient implements CatalogImportApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly scmAuthApi: ScmAuthApi;
  private readonly scmIntegrationsApi: ScmIntegrationRegistry;
  private readonly catalogApi: CatalogApi;
  private readonly configApi: ConfigApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    scmAuthApi: ScmAuthApi;
    fetchApi: FetchApi;
    scmIntegrationsApi: ScmIntegrationRegistry;
    catalogApi: CatalogApi;
    configApi: ConfigApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.scmAuthApi = options.scmAuthApi;
    this.fetchApi = options.fetchApi;
    this.scmIntegrationsApi = options.scmIntegrationsApi;
    this.catalogApi = options.catalogApi;
    this.configApi = options.configApi;
  }

  async assertImportAllowed(url: string): Promise<void> {
    const backendBaseUrl = await this.discoveryApi.getBaseUrl('catalog-import');
    const response = await this.fetchApi.fetch(
      `${backendBaseUrl}/github-import-allowed`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      },
    );

    if (!response.ok) {
      let errorMessage = `GitHub import is not allowed for ${url}`;
      try {
        const errorPayload = await response.json();
        if (errorPayload?.error?.message) {
          errorMessage = errorPayload.error.message;
        } else if (typeof errorPayload?.error === 'string') {
          errorMessage = errorPayload.error;
        }
      } catch {
        // ignore JSON parse errors
      }
      throw new Error(errorMessage);
    }
  }

  async analyzeUrl(url: string): Promise<AnalyzeResult> {
    if (
      new URL(url).pathname.match(/\.ya?ml$/) ||
      new URL(url).searchParams.get('path')?.match(/.ya?ml$/)
    ) {
      const location = await this.catalogApi.addLocation({
        type: 'url',
        target: url,
        dryRun: true,
      });

      if (!location.exists) {
        await this.assertImportAllowed(url);
      }

      return {
        type: 'locations',
        locations: [
          {
            exists: location.exists,
            target: location.location.target,
            entities: location.entities.map(e => ({
              kind: e.kind,
              namespace: e.metadata.namespace ?? 'default',
              name: e.metadata.name,
            })),
          },
        ],
      };
    }
    const supportedIntegrations = ['github', 'azure'];
    const foundIntegration = this.scmIntegrationsApi.byUrl(url);
    const iSupported =
      !!foundIntegration &&
      supportedIntegrations.find(it => it === foundIntegration.type);
    if (!iSupported) {
      const catalogFilename = getCatalogFilename(this.configApi);

      if (foundIntegration) {
        throw new Error(
          `The ${foundIntegration.title} integration only supports full URLs to ${catalogFilename} files. Did you try to pass in the URL of a directory instead?`,
        );
      }
      throw new Error(
        `This URL was not recognized as a valid git URL because there was no configured integration that matched the given host name. Currently GitHub and Azure DevOps are supported. You could try to paste the full URL to a ${catalogFilename} file instead.`,
      );
    }

    const analyzation = await this.analyzeLocation({
      repo: url,
    });

    if (analyzation.existingEntityFiles.length > 0) {
      const locations = analyzation.existingEntityFiles.reduce<
        Record<
          string,
          {
            target: string;
            exists?: boolean;
            entities: CompoundEntityRef[];
          }
        >
      >((state, curr) => {
        state[curr.location.target] = {
          target: curr.location.target,
          exists: curr.isRegistered,
          entities: [
            ...(curr.location.target in state
              ? state[curr.location.target].entities
              : []),
            {
              name: curr.entity.metadata.name,
              namespace: curr.entity.metadata.namespace ?? 'default',
              kind: curr.entity.kind,
            },
          ],
        };
        return state;
      }, {});
      return {
        type: 'locations',
        locations: Object.values(locations),
      };
    }

    return {
      type: 'repository',
      integrationType: foundIntegration.type,
      url: url,
      generatedEntities: analyzation.generateEntities.map(x => x.entity),
    };
  }

  async preparePullRequest(): Promise<{
    title: string;
    body: string;
  }> {
    const appTitle =
      this.configApi.getOptionalString('app.title') ?? 'Backstage';
    const appBaseUrl = this.configApi.getString('app.baseUrl');
    const catalogFilename = getCatalogFilename(this.configApi);

    return {
      title: `Add ${catalogFilename} config file`,
      body: `This pull request adds a **Backstage entity metadata file** \
to this repository so that the component can be added to the \
[${appTitle} software catalog](${appBaseUrl}).\n\nAfter this pull request is merged, \
the component will become available.\n\nFor more information, read an \
[overview of the Backstage software catalog](https://backstage.io/docs/features/software-catalog/).`,
    };
  }

  async submitPullRequest(options: {
    repositoryUrl: string;
    fileContent: string;
    title: string;
    body: string;
  }): Promise<{ link: string; location: string }> {
    const { repositoryUrl, fileContent, title, body } = options;

    const parseData = YAML.parseAllDocuments(fileContent);

    for (const document of parseData) {
      const validationResponse = await this.catalogApi.validateEntity(
        document.toJS(),
        `url:${repositoryUrl}`,
      );
      if (!validationResponse.valid) {
        throw new Error(validationResponse.errors[0].message);
      }
    }

    const provider = this.scmIntegrationsApi.byUrl(repositoryUrl);

    switch (provider?.type) {
      case 'github': {
        const { config } = provider as GithubIntegration;
        const { name, owner } = parseGitUrl(repositoryUrl);
        const options2: GitHubOptions = {
          githubIntegrationConfig: config,
          repo: name,
          owner: owner,
          repositoryUrl,
          fileContent,
          title,
          body,
        };
        return submitGitHubPrToRepo(options2, this.scmAuthApi, this.configApi);
      }
      case 'azure': {
        return submitAzurePrToRepo(
          {
            repositoryUrl,
            fileContent,
            title,
            body,
          },
          this.scmAuthApi,
          this.configApi,
        );
      }
      default: {
        throw new Error('unimplemented!');
      }
    }
  }

  async addTopicToRepo(options: {
    repositoryUrl: string;
    topic: string;
  }): Promise<{ repoUrl: string }> {
    const { repositoryUrl, topic } = options;

    const provider = this.scmIntegrationsApi.byUrl(repositoryUrl);

    switch (provider?.type) {
      case 'github': {
        const { config } = provider as GithubIntegration;
        const { name, owner } = parseGitUrl(repositoryUrl);
        return addTopicToRepo(
          {
            owner,
            repo: name,
            topic,
            repositoryUrl,
            githubIntegrationConfig: config,
          },
          this.scmAuthApi,
        );
      }
      default: {
        throw new Error(
          'Adding topics is only supported for GitHub repositories',
        );
      }
    }
  }

  async checkBeatId(repositoryUrl: string): Promise<{ hasBeatId: boolean }> {
    const provider = this.scmIntegrationsApi.byUrl(repositoryUrl);
    if (provider?.type !== 'github') {
      return { hasBeatId: false };
    }
    const { config } = provider as GithubIntegration;
    const { name: repo, owner } = parseGitUrl(repositoryUrl);
    const topics = await getRepoTopics(
      { owner, repo, repositoryUrl, githubIntegrationConfig: config },
      this.scmAuthApi,
    );
    return { hasBeatId: findBeatIdInTopics(topics) !== null };
  }

  async registerRepository(options: {
    repositoryUrl: string;
    division?: string;
    platform?: string;
  }): Promise<{ repoUrl: string; issueUrl: string; location: string }> {
    const { repositoryUrl, division: overrideDivision, platform: overridePlatform } = options;
    const provider = this.scmIntegrationsApi.byUrl(repositoryUrl);

    if (provider?.type !== 'github') {
      throw new Error(
        'Repository registration is only supported for GitHub repositories',
      );
    }

    const { config } = provider as GithubIntegration;
    const { name: repo, owner } = parseGitUrl(repositoryUrl);

    await this.assertImportAllowed(repositoryUrl);

    // Guard: refuse to create a duplicate issue if the catalog file is already
    // present in the repository (i.e. a previous PR/issue was already merged).
    const catalogFilename = getCatalogFilename(this.configApi);
    const catalogFileAlreadyExists = await checkCatalogFileExists(
      {
        owner,
        repo,
        filename: catalogFilename,
        repositoryUrl,
        githubIntegrationConfig: config,
      },
      this.scmAuthApi,
    );
    if (catalogFileAlreadyExists) {
      throw new Error(
        `${catalogFilename} already exists in this repository. ` +
          `The Backstage importer will register it automatically — no new issue is needed.`,
      );
    }

    // Guard: refuse to create a duplicate issue if a registration is already in
    // progress (open Copilot registration issue, or the integration branch
    // from the manual PR flow).
    const branchName = getBranchName(this.configApi);
    const registrationStatus = await getRegistrationStatus(
      { owner, repo, branchName, repositoryUrl, githubIntegrationConfig: config },
      this.scmAuthApi,
    );
    // Pre-compute the expected catalog location URL so the UI can offer an Import button.
    const expectedLocation = `https://${config.host}/${owner}/${repo}/blob/${registrationStatus.defaultBranch}/${catalogFilename}`;
    if (registrationStatus.isRegistrationInProgress) {
      if (registrationStatus.existingPrUrl) {
        throw new Error(
          `A Pull Request is already open for this repository: ${registrationStatus.existingPrUrl}\n__catalog_location__: ${expectedLocation}`,
        );
      }
      const inProgressReason = registrationStatus.openIssueUrl
        ? `a registration issue is already open: ${registrationStatus.openIssueUrl}`
        : `branch '${branchName}' already exists`;
      throw new Error(
        `A registration is already in progress — ${inProgressReason}. ` +
          `Please check your repository's open Pull Requests.\n__catalog_location__: ${expectedLocation}`,
      );
    }

    // Step 1: Add the backstage-candidate topic
    await addTopicToRepo(
      {
        owner,
        repo,
        topic: 'backstage-candidate',
        repositoryUrl,
        githubIntegrationConfig: config,
      },
      this.scmAuthApi,
    );

    // Step 2: Fetch all topics to look for a BEAT ID
    const topics = await getRepoTopics(
      {
        owner,
        repo,
        repositoryUrl,
        githubIntegrationConfig: config,
      },
      this.scmAuthApi,
    );

    const beatId = findBeatIdInTopics(topics);

    // Step 3: If there's a BEAT ID, call the backend for enrichment
    let beatApplication: BeatApplication | null = null;
    let platformResult: PlatformResult | null = null;

    if (beatId) {
      try {
        const backendBaseUrl = await this.discoveryApi.getBaseUrl(
          'catalog-import',
        );

        // Fetch BEAT application data
        const beatResponse = await this.fetchApi.fetch(
          `${backendBaseUrl}/beat-application`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ beatId: beatId.toUpperCase() }),
          },
        );

        if (beatResponse.ok) {
          const beatJson = await beatResponse.json();
          beatApplication = beatJson.data ?? null;
        } else {
          // eslint-disable-next-line no-console
          console.warn('[catalog-import] BEAT API error, status:', beatResponse.status);
        }

        // If we have a BEAT application, look up platform info
        if (beatApplication) {
          const cwid =
            beatApplication.businessOwnerCwid ||
            beatApplication.systemOwnerCwid ||
            beatApplication.systemOwnerDelegateCwid;

          if (cwid) {
            const platformResponse = await this.fetchApi.fetch(
              `${backendBaseUrl}/platform`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cwid }),
              },
            );

            if (platformResponse.ok) {
              const platformJson = await platformResponse.json();
              platformResult = platformJson.data ?? null;
            } else {
              // eslint-disable-next-line no-console
              console.warn('[catalog-import] Platform API error, status:', platformResponse.status);
            }
          }
        }
      } catch (enrichmentError: any) {
        // Non-fatal: we can still create the issue without enrichment
        // but log so users can diagnose issues
        // eslint-disable-next-line no-console
        console.warn(
          'BEAT/PAPI enrichment failed (issue will still be created with N/A values):',
          enrichmentError?.message ?? enrichmentError,
        );
      }
    }

    // Step 4: Suggest owners from the repo's admin/maintain teams.
    // Non-fatal: the prompt tells Copilot how to choose an owner without them.
    let suggestedOwners: string[] = [];
    try {
      const ownerTeamSlugs = await getRepoOwnerTeams(
        {
          owner,
          repo,
          repositoryUrl,
          githubIntegrationConfig: config,
        },
        this.scmAuthApi,
      );
      suggestedOwners = ownerTeamSlugs.map(slug => `group:default/${slug}`);
    } catch (ownerTeamsError: any) {
      // eslint-disable-next-line no-console
      console.warn(
        'Repository team lookup failed (issue will be created without suggested owners):',
        ownerTeamsError?.message ?? ownerTeamsError,
      );
    }

    // Step 5: Prepare the issue body from the template
    // Use BEAT-enriched values first; fall back to user-supplied overrides.
    const issueBody = prepareIssueBody(
      beatApplication,
      platformResult?.division ?? overrideDivision ?? null,
      platformResult?.platform ?? overridePlatform ?? null,
      suggestedOwners,
    );

    // Step 6: Create the GitHub Issue with Copilot SWE Agent assigned
    const { issueUrl } = await createIssueWithCopilotAgent(
      {
        owner,
        repo,
        title: REGISTRATION_ISSUE_TITLE,
        body: issueBody,
        labels: REGISTRATION_ISSUE_LABELS,
        repositoryUrl,
        githubIntegrationConfig: config,
      },
      this.scmAuthApi,
    );

    const location = `https://${config.host}/${owner}/${repo}/blob/${registrationStatus.defaultBranch}/${catalogFilename}`;

    return {
      repoUrl: `https://${config.host}/${owner}/${repo}`,
      issueUrl,
      location,
    };
  }

  // TODO: this could be part of the catalog api
  private async analyzeLocation(options: {
    repo: string;
  }): Promise<AnalyzeLocationResponse> {
    const response = await this.fetchApi
      .fetch(
        `${await this.discoveryApi.getBaseUrl('catalog')}/analyze-location`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify({
            location: { type: 'url', target: options.repo },
            ...(this.configApi.getOptionalString(
              'catalog.import.entityFilename',
            ) && {
              catalogFilename: this.configApi.getOptionalString(
                'catalog.import.entityFilename',
              ),
            }),
          }),
        },
      )
      .catch(e => {
        throw new Error(`Failed to generate entity definitions, ${e.message}`);
      });
    if (!response.ok) {
      let errorMessage = `Failed to generate entity definitions. Received http response ${response.status}: ${response.statusText}`;
      try {
        const errorPayload = await response.json();
        if (errorPayload?.error?.message) {
          errorMessage = errorPayload.error.message;
        }
      } catch (_) {
        // ignore JSON parse errors, fall back to generic message
      }
      throw new Error(errorMessage);
    }

    const payload = await response.json();
    return payload;
  }
}